import { useEffect, useRef, useState } from 'react';
import { SearchableSelect } from './SearchableSelect.jsx';
import { searchUniversities } from '../api.js';

/**
 * The University filter bar.
 *
 * Every filter the reference product's University page offers, over the same
 * `universities` collection it reads: free-text name search, an exact
 * University Name pick, university-country, course intake, course type,
 * location (campus), academic gap, highest qualification, and the three
 * toggles (interview required, tuition below 15k, accepts MOI).
 *
 * The long option lists (University Name, University Country, Course Type,
 * Location, Highest Qualification) are `SearchableSelect`s — a type-ahead
 * inside the dropdown, exactly as the reference has. The type-ahead only
 * narrows what is shown; it never changes the applied filter until an option
 * is chosen.
 *
 * Option labels carry UNIVERSITY counts — "United Kingdom (130)" is 130
 * universities — except University Name (one document per university, so the
 * count is always 1).
 */
function countsFrom(facet) {
  return new Map((facet ?? []).map((c) => [c.value, c.count]));
}

/**
 * Narrow an id-keyed option list to what the current search can return, using
 * the search-response facet — the same rule as the course rail's `narrow()`.
 * Skipped once the user has picked a value in this group, so the sibling they
 * might switch to is not hidden.
 */
function narrowId(options, idFacet, selectedId) {
  const list = [...(options ?? [])];
  const counts = countsFrom(idFacet);
  const withCounts = list
    .map((o) => ({ value: o.id, label: o.name, count: counts.get(o.id) ?? o.count ?? 0 }))
    .sort((a, b) => b.count - a.count);
  if (selectedId) {
    if (!withCounts.some((o) => o.value === selectedId)) {
      const picked = list.find((o) => o.id === selectedId);
      if (picked) withCounts.unshift({ value: selectedId, label: picked.name, count: 0 });
    }
    return withCounts;
  }
  if (counts.size === 0) return withCounts;
  return withCounts.filter((o) => o.count > 0 || o.value === selectedId);
}

/**
 * Narrow a dependent option list to what the current search can return, using
 * the search-response facet — the same rule as the course page's `narrow()`.
 * `all` items are `{ id, name, count? }`; the return keeps that shape with the
 * live count folded in.
 *
 * When contextOptions is supplied (e.g. Course Intake), selecting an intake
 * preserves that valid context-derived option set instead of expanding to the
 * catalogue-wide list.
 */
function narrow(all, facet, selected) {
  const contextOptions = arguments[3] ?? null;
  if (contextOptions && selected.length > 0) {
    const result = [...contextOptions];
    for (const selId of selected) {
      if (!result.some((o) => o.id === selId || o.value === selId)) {
        const found = (all ?? []).find((o) => o.id === selId || o.value === selId);
        if (found) result.push(found);
      }
    }
    return result;
  }
  const list = all ?? [];
  if (selected.length > 0) return list;
  const counts = new Map((facet ?? []).map((c) => [c.value, c.count]));
  if (counts.size === 0) return list;
  return list
    .filter((o) => (counts.get(o.id) ?? 0) > 0)
    .map((o) => ({ ...o, count: counts.get(o.id) }));
}

function Toggle({ id, label, checked, onChange, title }) {
  return (
    <label className="uni-toggle" htmlFor={id} title={title}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

export function UniversitySearchBar({ title = 'Search in Universities', filters, options, facets, loading = false, dispatch }) {
  // First load: `/api/universities/options` has not answered, so every list is
  // empty. `searchPending` also covers the intake list being rebuilt after a
  // parent filter moved.
  const optionsLoading = !options;
  const searchPending = optionsLoading || loading;
  const countryCounts = countsFrom(facets?.country_name);
  const nameCounts = countsFrom(facets?.name);

  const country = filters.countries[0] ?? '';
  const universityName = filters.universities[0] ?? '';

  const filterContextKey = [
    country,
    universityName,
    filters.courseTypeIds.slice().sort().join(','),
    filters.campusIds.slice().sort().join(','),
    filters.gapYear ?? '',
    filters.educationLevels.slice().sort().join(','),
    filters.interviewRequired ? '1' : '0',
    filters.tuitionBelow15k ? '1' : '0',
    filters.acceptsMoi ? '1' : '0',
    filters.q ?? '',
  ].join('|');

  const contextIntakesMapRef = useRef(new Map());
  const [, setIntakeUpdate] = useState(0);

  const intakeCounts = countsFrom(facets?.intake_ids);
  if (filters.intakeIds.length === 0 && intakeCounts.size > 0 && options?.intakeOptions) {
    const valid = options.intakeOptions
      .filter((o) => (intakeCounts.get(o.id) ?? 0) > 0)
      .map((o) => ({ ...o, count: intakeCounts.get(o.id) }));
    if (valid.length > 0) {
      contextIntakesMapRef.current.set(filterContextKey, valid);
    }
  }

  const contextIntakeOptions = contextIntakesMapRef.current.get(filterContextKey) ?? null;

  useEffect(() => {
    if (filters.intakeIds.length > 0 && !contextIntakesMapRef.current.has(filterContextKey)) {
      searchUniversities({
        ...filters,
        intakeIds: [],
        perPage: 0,
      }).then((res) => {
        const counts = countsFrom(res.facets?.intake_ids);
        if (counts.size > 0 && options?.intakeOptions) {
          const valid = options.intakeOptions
            .filter((o) => (counts.get(o.id) ?? 0) > 0)
            .map((o) => ({ ...o, count: counts.get(o.id) }));
          if (valid.length > 0) {
            contextIntakesMapRef.current.set(filterContextKey, valid);
            setIntakeUpdate((v) => v + 1);
          }
        }
      }).catch(() => {});
    }
  }, [filterContextKey, filters, options?.intakeOptions]);

  // Exact intakes — narrowed to what the current search can return via the live
  // `intake_ids` facet, preserved across selections for the current context.
  const exactIntakeOptions = narrow(
    options?.intakeOptions, facets?.intake_ids, filters.intakeIds, contextIntakeOptions,
  ).map((o) => ({ value: o.id, label: o.name, count: o.count ?? 0 }));

  const toggleMany = (key, next, current) => {
    const cur = new Set(current);
    const nxt = new Set(next);
    for (const v of nxt) if (!cur.has(v)) dispatch({ type: 'TOGGLE', key, value: v });
    for (const v of cur) if (!nxt.has(v)) dispatch({ type: 'TOGGLE', key, value: v });
  };

  // Country options: full known list, disabled at zero unless selected.
  const countryOptions = (options?.countries ?? []).map((c) => ({
    value: c.value,
    label: c.value,
    count: countryCounts.get(c.value) ?? 0,
  }));

  /**
   * University Name options.
   *
   * `options.universities` is every in-scope university (one per row). While
   * nothing is picked the list is narrowed to those still reachable under the
   * other filters (`facets.name`); once one is picked the full list is shown so
   * it can be changed. The selected value is always present and selectable.
   */
  const universityOptions = (() => {
    const all = (options?.universities ?? []).map((u) => ({ value: u.value, label: u.value }));
    if (universityName) {
      if (!all.some((o) => o.value === universityName)) {
        all.unshift({ value: universityName, label: universityName });
      }
      return all;
    }
    if (nameCounts.size === 0) return all;
    return all.filter((o) => nameCounts.has(o.value));
  })();

  return (
    <div className="searchbar uni-searchbar">
      <div className="searchbar-row-keyword">
        {title && <h1 id="uni-title" className="filterbar-title-inline">{title}</h1>}
        <div className="fld fld-keyword">
          <label htmlFor="uq">Search by name</label>
          <div className="keyword-input-wrap">
            <span className="keyword-search-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              id="uq"
              type="search"
              placeholder="e.g. University of Leeds"
              autoComplete="off"
              value={filters.q}
              onChange={(e) => dispatch({ type: 'SET_QUERY', value: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="searchbar-row-secondary">
        <SearchableSelect
          id="uUniversity"
          label="University name"
          placeholder="All universities"
          searchPlaceholder="Search universities…"
          loading={optionsLoading}
          options={universityOptions}
          value={universityName}
          showCounts={false}
          onChange={(v) => dispatch({ type: 'SET_UNIVERSITY', value: v })}
          emptyText="No universities in scope"
        />

        <SearchableSelect
          id="uCountry"
          label="University country"
          placeholder="All countries"
          searchPlaceholder="Search countries…"
          showCounts={false}
          // The parent filter: must always be changeable — see the
          // `disableZeroCount` doc comment in SearchableSelect.jsx.
          disableZeroCount={false}
          loading={optionsLoading}
          options={countryOptions}
          value={country}
          onChange={(v) => dispatch({ type: 'SET_COUNTRY', value: v })}
        />

        <SearchableSelect
          id="uCourseIntake"
          label="Course intake (exact)"
          placeholder="Any intake"
          searchPlaceholder="Search intakes…"
          multiple
          showCounts={false}
          loading={searchPending}
          options={exactIntakeOptions}
          value={filters.intakeIds}
          onChange={(next) => toggleMany('intakeIds', next, filters.intakeIds)}
        />

        <SearchableSelect
          id="uCourseType"
          label="Course type"
          placeholder="All course types"
          searchPlaceholder="Search course types…"
          showCounts={false}
          loading={optionsLoading}
          options={narrowId(options?.courseTypes, facets?.course_level_ids, filters.courseTypeIds[0] ?? '')}
          value={filters.courseTypeIds[0] ?? ''}
          onChange={(v) => dispatch({ type: 'SET_COURSE_TYPE', value: v })}
        />

        <SearchableSelect
          id="uLocation"
          label="Campus location"
          placeholder="Any campus location"
          searchPlaceholder="Search campus locations…"
          showCounts={false}
          loading={optionsLoading}
          options={narrowId(options?.campuses, facets?.campus_ids, filters.campusIds[0] ?? '')}
          value={filters.campusIds[0] ?? ''}
          onChange={(v) => dispatch({ type: 'SET_LOCATION', value: v })}
        />
      </div>

      <div className="searchbar-row-tertiary">
        <SearchableSelect
          id="uGap"
          label="Academic gap"
          placeholder="Any gap"
          searchPlaceholder="Search…"
          showCounts={false}
          loading={optionsLoading}
          options={(options?.gapYears ?? []).map((g) => ({ value: String(g.years), label: g.name }))}
          value={filters.gapYear}
          onChange={(v) => dispatch({ type: 'SET_GAP_YEAR', value: v })}
        />

        <SearchableSelect
          id="uQualification"
          label="Highest qualification"
          placeholder="Any qualification"
          searchPlaceholder="Search qualifications…"
          showCounts={false}
          loading={optionsLoading}
          options={narrowId(options?.highestQualifications, facets?.education_levels, filters.educationLevels[0] ?? '')}
          value={filters.educationLevels[0] ?? ''}
          onChange={(v) => dispatch({ type: 'SET_QUALIFICATION', value: v })}
        />

        <div className="uni-toggles">
          <Toggle
            id="uInterview"
            label="Interview required"
            title="Universities that run a credibility interview. Read from the university's
Credibility Interview switch — the same one the reference filters on with
credibilityInterview=1 — not from offer-process prose."
            checked={filters.interviewRequired}
            count={options?.interviewCount}
            onChange={(v) => dispatch({ type: 'SET_FLAG', key: 'interviewRequired', value: v })}
          />
          <Toggle
            id="uAffordable"
            label="Tuition fee below 15k"
            checked={filters.tuitionBelow15k}
            count={options?.affordableCount}
            onChange={(v) => dispatch({ type: 'SET_FLAG', key: 'tuitionBelow15k', value: v })}
          />
          <Toggle
            id="uMoi"
            label="Accepting MOI"
            checked={filters.moiAccepted}
            count={options?.moiCount}
            onChange={(v) => dispatch({ type: 'SET_FLAG', key: 'moiAccepted', value: v })}
          />
        </div>
      </div>
    </div>
  );
}
